import React, { useMemo, useState, useCallback } from 'react';

import {
  Stack, Button, Select, Tooltip, Popover, MenuItem,
  useTheme, TextField, IconButton, InputLabel,
  FormControl, useMediaQuery, InputAdornment
} from '@mui/material';

import { useTranslate } from 'src/locales';

import Iconify from 'src/components/iconify';
import { TableProps } from 'src/components/table';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { TenantType } from 'src/types/enums';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { Box } from '@mui/system';
import TableSorter from 'src/components/sorter/table-sorter';
import { SortOrder } from 'src/services/common.types';

export interface ToolbarOptions {
  addButton: boolean;
}

export type Props = {
  table: TableProps;
  onAddContact?: () => void;
  onFilters: (type: string, value: string) => void;
  clearFilters: () => void;
  onAddToList?: (contactIds: string[]) => void;
  onSendMultiEmail?: (contactIds: string[]) => void;
  onBulkImport?: () => void;
  onSortChange?: (sortConfig: { sort_by: string; sort_order: SortOrder }) => void;
};

export default function ContactsTableToolbar({
  table,
  onAddContact,
  onFilters,
  onSortChange,
  clearFilters,
  onAddToList,
  onBulkImport,
  onSendMultiEmail
}: Props) {

  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const tenantType = useOrgTenant();
  const { t } = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const actionsControl = usePopover();

  const onClearFilters = () => {
    setSearchValue('');
    setSelectedType(null);
    clearFilters();
  };

  const handleFilterName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFilters('search_query', event.target.value);
      setSearchValue(event.target.value);
    },
    [onFilters]
  );

  const handleFilterType = useCallback(
    (type: string) => {
      onFilters('type', type);
      setSelectedType(type);
    },
    [onFilters]
  );

  const handleOpenFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setAnchorEl(null);
  };

  const disableReset = useMemo(() => !searchValue && !selectedType, [searchValue, selectedType]);

  const showActions = useMemo(() => !!onAddContact, [onAddContact]);

  const filterContent = (
    <Stack spacing={2} display="flex" direction={{ xs: 'column', sm: 'row' }} sx={{ justifyContent: 'center', alignItems: 'center', alignContent: 'center' }}>
      <TextField
        onChange={handleFilterName}
        placeholder={t('common.placeholders.search')}
        value={searchValue}
        name="search"
        sx={{ width: { sm: '500px', xs: '100%' } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ),
        }}
      />
      <FormControl sx={{ width: '100%' }}>
        <InputLabel id="contact-type-select-label">
          {t('common.type')}
        </InputLabel>
        <Select
          id="contact-type-select"
          label={t('common.placeholders.contact_type')}
          value={selectedType || ''}
          onChange={(event) => handleFilterType(event.target.value as TenantType)}
          placeholder={t('common.placeholders.contact_type')}
        >
          <MenuItem value="">{t('common.none')}</MenuItem>
          {Object.values(TenantType).map((value) => (
            <MenuItem key={value} value={value}>
              {t(`enums.tenant.${value}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {isMobile ? (
        <Button
          variant="text"
          disabled={disableReset}
          onClick={onClearFilters}
        >
          {t('common.clear')}
        </Button>
      ) :
        <FormControl>
          <Tooltip title={t('common.clear')}>
            <IconButton
              size="large"
              disabled={disableReset}
              onClick={onClearFilters}
              aria-label={t('common.clear')}
            >
              <Iconify icon="material-symbols:cancel" />
            </IconButton>
          </Tooltip>
        </FormControl>
      }
    </Stack>
  );

  return (
    <Stack
      spacing={2}
      display="flex"
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      justifyContent={{ xs: 'center', sm: 'space-between' }}
      sx={{
        p: 2,
        width: '100%',
      }}
    >
      {isMobile ? (
        <>
          <Button
            variant="outlined"
            onClick={handleOpenFilter}
            fullWidth
            startIcon={<Iconify icon="mdi:filter" />}
          >
            {t('common.filter')}
          </Button>
          <Popover
            sx={{ padding: 2 }}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleCloseFilter}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {filterContent}
          </Popover>
        </>
      ) : (
        <Stack sx={{ justifyContent: 'space-between', flexDirection: 'row', alignItems: 'center', 'width': '100%' }}>
          {filterContent}
        </Stack>
      )}

      <Stack direction="row" spacing={1}>
        <Box sx={{ display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' } }}>
          {
            onSortChange && (
              <TableSorter
                sortOptions={[
                  { key: 'name', label: t('contacts.sorters.name') },
                  { key: 'company', label: t('contacts.sorters.company') },
                  { key: 'created_at', label: t('contacts.sorters.created_at') },
                  { key: 'updated_at', label: t('contacts.sorters.updated_at') },
                ]}
                onChange={(sort) => onSortChange?.(sort)}
              />
            )
          }
        </Box>

        {
          showActions && (
            <Button
              size="medium"
              onClick={actionsControl.onOpen}
              startIcon={<Iconify icon="solar:hamburger-menu-line-duotone" />}
              variant="outlined"
              sx={{ textWrap: 'nowrap' }}
            >
              {t('common.actions')}
            </Button>
          )
        }
      </Stack>

      <CustomPopover
        open={actionsControl.open}
        onClose={actionsControl.onClose}
        arrow="top-right"
      >
        <Stack>
          {onAddContact && (
            <MenuItem
              onClick={() => {
                actionsControl.onClose();
                onAddContact?.();
              }}
            >
              <Iconify icon="material-symbols:add" />
              {t(`contacts.add`)}
            </MenuItem>
          )}

          {
            onAddToList && (
              <Tooltip title={!table?.selected.length ? t('lists.bulk_add_disabled_tootip') : ''}>
                <span>
                  <MenuItem
                    disabled={!table?.selected.length}
                    onClick={() => {
                      actionsControl.onClose();
                      onAddToList?.(table?.selected ?? []);
                    }}
                  >
                    <Iconify icon="material-symbols:contextual-token-add-outline" />
                    {t('lists.add_to_list')}
                  </MenuItem>
                </span>
              </Tooltip>
            )
          }

          {
            (onSendMultiEmail && [TenantType.Client, TenantType.Recruiter].includes(tenantType)) && (
              <Tooltip title={!table?.selected.length ? t('contacts.send_bulk_mail_tooltip') : ''}>
                <span>
                  <MenuItem
                    disabled={!table?.selected.length}
                    onClick={() => {
                      actionsControl.onClose();
                      onSendMultiEmail?.(table?.selected ?? []);
                    }}
                  >
                    <Iconify icon="iconoir:send-mail" />
                    {t('contacts.send_bulk_mail')}
                  </MenuItem>
                </span>
              </Tooltip>
            )
          }

          {onBulkImport && (
            <MenuItem
              onClick={() => {
                actionsControl.onClose();
                onBulkImport?.();
              }}
            >
              <Iconify icon="stash:file-import-duotone" />
              {t(`contacts.bulk_import`)}
            </MenuItem>
          )}

        </Stack>
      </CustomPopover >

    </Stack>
  );
}